import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import {
  ProvisioningOverlayMode,
  ProvisioningOverlayState,
} from "../../hooks/useCompanyProvisioning";
import styles from "./provisioningOverlay.module.css";

interface ProvisioningOverlayProps {
  overlay: ProvisioningOverlayState;
  onRetry?: () => void;
  onExit?: () => void;
}

const PROVISIONING_TIP_KEYS = [
  "COMPANY_PROVISIONING_TIP_1",
  "COMPANY_PROVISIONING_TIP_2",
  "COMPANY_PROVISIONING_TIP_3",
  "COMPANY_PROVISIONING_TIP_4",
];

const TIP_ROTATION_MS = 3200;

function ProvisioningOverlay({
  overlay,
  onRetry,
  onExit,
}: ProvisioningOverlayProps) {
  const isProcessing = overlay.mode === "processing";
  const isSuccess = overlay.mode === "success";
  const isFailed = overlay.mode === "failed";

  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    if (!isProcessing) {
      return undefined;
    }
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % PROVISIONING_TIP_KEYS.length);
    }, TIP_ROTATION_MS);
    return () => clearInterval(interval);
  }, [isProcessing]);

  if (!overlay.visible) {
    return null;
  }

  const modalRoot = document.getElementById("modals") as HTMLDivElement | null;

  const content = (
    <div
      className={styles.overlay}
      role="alertdialog"
      aria-modal="true"
      aria-busy={isProcessing}
      aria-live="polite"
      aria-label={
        isFailed ? t("COMPANY_PROVISIONING_FAILED_TITLE") : overlay.message
      }
    >
      <div
        className={`${styles.panel} ${isFailed ? styles.panelFailed : ""} ${
          isSuccess ? styles.panelSuccess : ""
        }`}
        dir={i18n.dir()}
      >
        {isProcessing && (
          <>
            <div className={styles.iconWrap}>
              <div className={styles.spinner} aria-hidden="true" />
            </div>
            <h2 className={styles.title}>{t("COMPANY_PROVISIONING_TITLE")}</h2>
            <p className={styles.message}>{overlay.message}</p>
            <div className={styles.progressTrack} aria-hidden="true">
              <div className={styles.progressBar} />
            </div>
            <p className={styles.tip} key={tipIndex} aria-live="off">
              {t(PROVISIONING_TIP_KEYS[tipIndex])}
            </p>
          </>
        )}

        {isSuccess && (
          <>
            <div className={`${styles.iconWrap} ${styles.iconWrapSuccess}`}>
              <svg
                className={styles.checkIcon}
                viewBox="0 0 52 52"
                aria-hidden="true"
              >
                <circle
                  className={styles.checkCircle}
                  cx="26"
                  cy="26"
                  r="23"
                  fill="none"
                />
                <path
                  className={styles.checkMark}
                  fill="none"
                  d="M14.5 27l7 7 16-16"
                />
              </svg>
            </div>
            <h2 className={`${styles.title} ${styles.titleSuccess}`}>
              {t("COMPANY_PROVISIONING_SUCCESS_TITLE")}
            </h2>
            <p className={styles.message}>{overlay.message}</p>
          </>
        )}

        {isFailed && (
          <>
            <div className={`${styles.iconWrap} ${styles.iconWrapFailed}`}>
              <span className={styles.errorIcon} aria-hidden="true">
                !
              </span>
            </div>
            <h2 className={`${styles.title} ${styles.titleFailed}`}>
              {t("COMPANY_PROVISIONING_FAILED_TITLE")}
            </h2>
            {overlay.errorMessage && (
              <p className={styles.errorBox}>{overlay.errorMessage}</p>
            )}
            <div className={styles.actions}>
              {onRetry && (
                <button
                  type="button"
                  className={`button hover ${styles.button} ${styles.buttonRetry}`}
                  onClick={onRetry}
                >
                  {t("COMPANY_PROVISIONING_RETRY")}
                </button>
              )}
              {onExit && (
                <button
                  type="button"
                  className={`button ${styles.button} ${styles.buttonExit}`}
                  onClick={onExit}
                >
                  {t("COMPANY_PROVISIONING_EXIT")}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (modalRoot) {
    return ReactDOM.createPortal(content, modalRoot);
  }

  return content;
}

export type { ProvisioningOverlayMode };
export default ProvisioningOverlay;
